import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, GitBranch, Sparkles, BookOpenText, Terminal, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { getStats, clearToken } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/roadmap", label: "Roadmap", icon: GitBranch, testid: "nav-roadmap" },
  { to: "/assistant", label: "Coach", icon: Sparkles, testid: "nav-assistant" },
  { to: "/journal", label: "Journal", icon: BookOpenText, testid: "nav-journal" },
];

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <Terminal className="h-4 w-4 text-primary" />
      <span className="mono text-sm tracking-[0.2em] uppercase text-primary terminal-glow">Consultant.OS</span>
    </div>
  );
}

export default function AppShell() {
  const [stats, setStats] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let live = true;
    getStats().then((s) => live && setStats(s)).catch(() => {});
    return () => { live = false; };
  }, [location.pathname]);

  const lock = () => { clearToken(); window.location.reload(); };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background text-foreground">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <Logo />
        <div className="flex items-center gap-3">
          {stats && (
            <span className="mono text-xs text-primary" data-testid="mobile-streak">{stats.streak_days ?? 0}d · {stats.xp ?? 0}xp</span>
          )}
          <button data-testid="mobile-logout-btn" onClick={lock} className="text-muted-foreground hover:text-destructive">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="w-60 shrink-0 border-r border-border bg-card hidden md:flex md:flex-col">
        <div className="px-6 py-6 border-b border-border">
          <Logo />
          <p className="mono text-[10px] mt-2 text-muted-foreground tracking-[0.2em]">v0.1 — 30 DAY BOOTCAMP</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-muted text-primary border border-border"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        {stats && (
          <div className="px-4 py-4 border-t border-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="overline">Streak</span>
              <span className="mono text-sm text-primary">{stats.streak_days ?? 0}d</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="overline">XP</span>
              <span className="mono text-sm">{stats.xp ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="overline">Day</span>
              <span className="mono text-sm">{stats.day_number ?? 1}/30</span>
            </div>
            <Badge variant="outline" className="w-full justify-center mono text-[10px] border-border">
              {stats.title || "Curious Recruit"}
            </Badge>
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={lock}
          className="flex items-center gap-2 px-4 py-3 border-t border-border text-xs text-muted-foreground hover:text-destructive transition-colors duration-150"
        >
          <LogOut className="h-3.5 w-3.5" /> Lock
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 grid-bg">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 md:px-10 py-6 md:py-8 pb-24 md:pb-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 flex border-t border-border bg-card pb-[env(safe-area-inset-bottom)]">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`mobile-${testid}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-2.5 text-[10px] mono uppercase tracking-wide transition-colors duration-150 ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
